import { useEffect, useState } from "react";
import axios from "axios";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { GiftCardItem } from "@/components/GiftCardItem";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Shield, Zap, CreditCard, CheckCircle, DollarSign, ShoppingCart, ExternalLink, Wallet, Coins, ShoppingBag, MessageCircle, Package, Truck, Store } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { VirtualCardModal } from "@/pages/linkamazon/VirtualCardModal";
import { CryptoRechargeModal } from "@/pages/linkamazon/CryptoRechargeModal";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

// Conversion rate: 1 USD = 148 HTG (approximate)
const USD_TO_HTG_RATE = 148;

export const HomePage = () => {
  const navigate = useNavigate();
  const [inventory, setInventory] = useState({});
  const [cards, setCards] = useState([]);
  const [marketplaceProducts, setMarketplaceProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showMeruModal, setShowMeruModal] = useState(false);
  const [showCryptoModal, setShowCryptoModal] = useState(false);

  useEffect(() => {
    fetchInventory();

    // Check for meru=true in URL
    const params = new URLSearchParams(window.location.search);
    if (params.get('meru') === 'true') {
      setShowMeruModal(true);
    }
  }, []);

  const fetchInventory = async () => {
    try {
      const [inventoryRes, cardsRes, mktRes] = await Promise.all([
        axios.get(`${API}/gift-cards/inventory`),
        axios.get(`${API}/gift-cards`),
        axios.get(`${API}/marketplace/products`).catch(() => ({ data: [] }))
      ]);
      setInventory(inventoryRes.data);

      // Group cards by denomination and pick one available card per denomination
      const cardsByDenom = {};
      cardsRes.data.forEach(card => {
        if (!card.is_sold && !cardsByDenom[card.denomination]) {
          cardsByDenom[card.denomination] = card;
        }
      });

      // Sort by denomination
      const sortedCards = Object.values(cardsByDenom).sort((a, b) => a.denomination - b.denomination);
      setCards(sortedCards);

      // GROUP MARKETPLACE PRODUCTS: One representative per store (User request)
      const allMktProducts = mktRes.data || [];
      const storeGroups = {};
      allMktProducts.forEach(p => {
          const sid = (p.seller_id || "").toString().trim();
          if (sid && !storeGroups[sid]) {
              storeGroups[sid] = p; // Take the first one (latest)
          }
      });
      setMarketplaceProducts(Object.values(storeGroups));
    } catch (error) {
      console.error("Error fetching inventory:", error);
    } finally {
      setLoading(false);
    }
  };

  // Helper to group marketplace products into 2x2 cards
  const getGroupedProducts = () => {
    // We want 4 items per card. Let's group by category or simply chunks of 4.
    const chunks = [];
    const chunkSize = 4;
    for (let i = 0; i < marketplaceProducts.length; i += chunkSize) {
      chunks.push(marketplaceProducts.slice(i, i + chunkSize));
    }
    return chunks.slice(0, 4); // Show top 4 cards
  };

  const handleMeruClick = () => {
    setShowMeruModal(true);
  };

  const handleCryptoClick = () => {
    const token = localStorage.getItem("buyer_token");
    if (!token) {
      toast.info("Tanpri konekte oswa kreye yon kont pou achte USDT.");
      navigate("/linkamazon/login");
    } else {
      setShowCryptoModal(true);
    }
  };


  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-HT', {
      style: 'decimal',
      minimumFractionDigits: 0,
    }).format(price);
  };

  const features = [
    {
      icon: Shield,
      title: "Sekirite Garanti",
      description: "Tout tranzaksyon yo pwoteje ak teknoloji modèn."
    },
    {
      icon: Zap,
      title: "Livrezon Imedya",
      description: "Jwenn kòd kat kado ou a touswit apre peman."
    },
    {
      icon: CreditCard,
      title: "Peye ak Moncash",
      description: "Itilize kont Moncash ou pou peye fasil."
    }
  ];

  return (
    <div className="min-h-screen flex flex-col" data-testid="home-page">
      <Header />

      {/* Hero Section */}
      <section data-testid="hero-section">
        {/* === DESKTOP VERSION: overlay style === */}
        <div className="relative overflow-hidden hidden md:block">
          <div className="absolute inset-0">
            <img src="/bannersa.png?v=3" alt="BVG Link Amazon" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/30 to-transparent" />
          </div>
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 min-h-[600px] flex items-center">
            <div className="max-w-xl">
              <div className="mb-8">
                <img src="/logo.png?v=4" alt="BVGLink Biznis" className="h-28 w-auto" style={{ filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.4))' }} />
              </div>

              <p className="text-xl text-white/90 mb-8 max-w-md font-medium" style={{ textShadow: '1px 1px 4px rgba(0,0,0,0.5)' }}>
                Eksplore makèt sou entènèt nou an pou w achte pi bon pwodwi yo ak kat kado san strès. Peye an sekirite ak <strong className="text-[#FF9900]">Moncash</strong>, <strong className="text-[#FF9900]">Zelle</strong> oswa <strong className="text-[#FF9900]">Virement</strong>.
              </p>
              <div className="flex flex-row flex-wrap gap-3">
                <a href="/linkamazon" className="inline-flex items-center justify-center gap-2 bg-white/95 text-[#232F3E] px-5 py-3 rounded-2xl font-bold text-base hover:bg-white transition-all hover:scale-105 shadow-2xl" data-testid="cta-amazon">
                  <ExternalLink className="w-4 h-4" /> Login
                </a>
                <a
                  href="/marketplace"
                  className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#FF9900] to-[#E88B00] text-white px-8 py-4 rounded-2xl font-bold text-lg hover:opacity-90 transition-all hover:scale-105 shadow-2xl"
                  data-testid="cta-marketplace"
                >
                  <ShoppingBag className="w-5 h-5" /> Marketplace
                </a>
                <a
                  href="/linklicards"
                  className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-cyan-500 via-teal-500 to-emerald-500 text-slate-950 px-7 py-4 rounded-2xl font-black text-base hover:opacity-95 transition-all hover:scale-105 shadow-2xl uppercase tracking-wider"
                  data-testid="cta-linklicards"
                >
                  <CreditCard className="w-5 h-5 text-slate-950" /> LINKLI CARDS
                </a>
                <a href="/seller/login" className="inline-flex items-center justify-center gap-2 bg-white/95 text-[#232F3E] px-5 py-3 rounded-2xl font-bold text-base hover:bg-white transition-all hover:scale-105 shadow-2xl">
                  <Store className="w-4 h-4 text-[#FF9900]" /> KONEKTE MACHANN
                </a>
                <a href="/driver/login" className="inline-flex items-center justify-center gap-2 bg-white/95 text-[#232F3E] px-5 py-3 rounded-2xl font-bold text-base hover:bg-white transition-all hover:scale-105 shadow-2xl">
                  <Truck className="w-4 h-4 text-[#FF9900]" /> DASHBOARD CHOFÈ
                </a>
              </div>
              <div className="flex flex-wrap items-center gap-3 mt-8">
                <div className="flex items-center gap-1.5 bg-white/20 backdrop-blur-md px-4 py-2 rounded-full text-sm font-semibold text-white border border-white/20"><CheckCircle className="w-4 h-4 text-green-400" /> Sekirize 100%</div>
                <div className="flex items-center gap-1.5 bg-white/20 backdrop-blur-md px-4 py-2 rounded-full text-sm font-semibold text-white border border-white/20"><Zap className="w-4 h-4 text-[#FF9900]" /> Livrezon Imedya</div>
                <div className="flex items-center gap-1.5 bg-white/20 backdrop-blur-md px-4 py-2 rounded-full text-sm font-semibold text-white border border-white/20"><CreditCard className="w-4 h-4 text-blue-400" /> Moncash · Zelle</div>
              </div>
            </div>
          </div>
        </div>

        {/* === MOBILE VERSION: image on top, text below (NO overlap!) === */}
        <div className="md:hidden">
          <div className="relative h-[350px] overflow-hidden">
            <img src="/bannersa.png?v=3" alt="BVG Link Amazon" className="w-full h-full object-cover object-[60%_top]" />
            <div className="absolute top-4 left-4">
              <img src="/logo.png?v=4" alt="BVGLink Biznis" className="h-14 w-auto" style={{ filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.4))' }} />
            </div>
          </div>
          <div className="bg-[#232F3E] px-5 py-8">

            <p className="text-base text-gray-300 mb-6 leading-relaxed">
              Eksplore makèt sou entènèt nou an pou w achte pi bon pwodwi yo ak kat kado san strès. Peye an sekirite ak <strong className="text-[#FF9900]">Moncash</strong>, <strong className="text-[#FF9900]">Zelle</strong> oswa <strong className="text-[#FF9900]">Virement</strong>.
            </p>
            <div className="flex flex-col gap-3">
              <a href="/linkamazon" className="inline-flex items-center justify-center gap-2 bg-white text-[#232F3E] px-4 py-3 rounded-2xl font-bold text-base shadow-xl" data-testid="cta-amazon-mobile">
                <ExternalLink className="w-4 h-4" /> Login
              </a>
              <a
                href="/marketplace"
                className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#FF9900] to-[#E88B00] text-white px-6 py-4 rounded-2xl font-bold text-lg shadow-xl"
                data-testid="cta-marketplace-mobile"
              >
                <ShoppingBag className="w-5 h-5" /> Marketplace
              </a>
              <a
                href="/linklicards"
                className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-cyan-500 via-teal-500 to-emerald-500 text-slate-950 px-6 py-3.5 rounded-2xl font-black text-base shadow-xl uppercase tracking-wider text-center"
                data-testid="cta-linklicards-mobile"
              >
                <CreditCard className="w-5 h-5 text-slate-950" /> LINKLI CARDS
              </a>
              <a href="/seller/login" className="inline-flex items-center justify-center gap-2 bg-white text-[#232F3E] px-4 py-3 rounded-2xl font-bold text-base shadow-xl">
                <Store className="w-4 h-4 text-[#FF9900]" /> KONEKTE MACHANN
              </a>
              <a href="/driver/login" className="inline-flex items-center justify-center gap-2 bg-white text-[#232F3E] px-4 py-3 rounded-2xl font-bold text-base shadow-xl">
                <Truck className="w-4 h-4 text-[#FF9900]" /> DASHBOARD CHOFÈ
              </a>
            </div>
            <div className="flex flex-wrap items-center gap-2 mt-6">
              <div className="flex items-center gap-1.5 bg-white/10 px-3 py-1.5 rounded-full text-xs font-semibold text-gray-300 border border-white/10"><CheckCircle className="w-3.5 h-3.5 text-green-400" /> Sekirize</div>
              <div className="flex items-center gap-1.5 bg-white/10 px-3 py-1.5 rounded-full text-xs font-semibold text-gray-300 border border-white/10"><Zap className="w-3.5 h-3.5 text-[#FF9900]" /> Livrezon Imedya</div>
              <div className="flex items-center gap-1.5 bg-white/10 px-3 py-1.5 rounded-full text-xs font-semibold text-gray-300 border border-white/10"><CreditCard className="w-3.5 h-3.5 text-blue-400" /> Moncash · Zelle</div>
            </div>
          </div>
        </div>
      </section>

      {/* LINKLI CARDS BRANDED PROMO BANNER */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6">
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-[#0d3b66] via-[#001e3d] to-[#0a1128] p-5 sm:p-6 text-white shadow-xl border border-cyan-500/30 flex flex-col md:flex-row items-center justify-between gap-5 transition-all hover:shadow-2xl">
          <div className="flex flex-col sm:flex-row items-center gap-4 text-center sm:text-left">
            <div className="w-16 h-16 rounded-2xl bg-white/10 backdrop-blur-md p-2 flex items-center justify-center border border-white/20 shadow-inner flex-shrink-0">
              <img 
                src="/logo-linkcards.png" 
                alt="LINKLI CARDS Logo" 
                className="max-h-full max-w-full object-contain filter drop-shadow"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = "/linkli-cards-logo.png";
                }}
              />
            </div>
            <div>
              <div className="flex items-center justify-center sm:justify-start gap-2">
                <h3 className="text-xl sm:text-2xl font-black tracking-tight bg-gradient-to-r from-cyan-400 via-teal-300 to-emerald-400 bg-clip-text text-transparent">
                  LINKLI CARDS
                </h3>
                <span className="px-2 py-0.5 text-[10px] font-bold bg-cyan-500/20 text-cyan-300 border border-cyan-500/40 rounded-full uppercase">
                  Kat & Portfèy
                </span>
              </div>
              <p className="text-gray-300 text-xs sm:text-sm mt-1 max-w-md">
                Kreye kat vityèl, voye kòb, epi peye sou Amazon, Shein, eBay, Netflix ak tout sit entènasyonal san limit.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3 w-full sm:w-auto">
            <a
              href="/linklicards"
              className="w-full sm:w-auto text-center px-6 py-3.5 bg-gradient-to-r from-cyan-500 to-emerald-500 hover:from-cyan-400 hover:to-emerald-400 text-black font-black rounded-2xl shadow-lg hover:shadow-cyan-500/25 transition-all duration-300 flex items-center justify-center gap-2 uppercase tracking-wider text-xs sm:text-sm cursor-pointer no-underline"
            >
              <CreditCard className="w-4 h-4" />
              Ouvri Linkli Cards
              <ExternalLink className="w-4 h-4" />
            </a>
          </div>
        </div>
      </div>



      {/* Features Section */}
      <section className="py-16 bg-white" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="bg-gray-50 p-8 rounded-2xl border border-transparent hover:border-[#FF9900]/20 transition-colors"
                data-testid={`feature-${index}`}
              >
                <div className="w-14 h-14 rounded-xl bg-[#FF9900] flex items-center justify-center mb-4">
                  <feature.icon className="w-7 h-7 text-black" />
                </div>
                <h3 className="text-xl font-bold text-[#232F3E] mb-2">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Amazon Style Marketplace Cards Section */}
      {!loading && marketplaceProducts.length > 0 && (
        <section className="py-8 bg-gray-100/50" data-testid="amazon-style-marketplace">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {getGroupedProducts().map((group, groupIdx) => {
                const categoryNames = {
                  phones: "Telefòn & Tablet",
                  clothing: "Fashion & Rad",
                  digital: "Digital & Kont",
                  electronics: "Elektwonik",
                  services: "Sèvis & Plis"
                };
                const titles = ["Dekouvri Pwodwi Nou Yo", "Pwodwi Elektwonik", "Pwodwi Pou Ou", "Chwa Marketplace la"];
                return (
                  <div key={groupIdx} className="bg-white p-5 rounded-sm shadow-sm flex flex-col items-start hover:shadow-md transition-shadow">
                    <h3 className="text-[21px] font-bold text-[#0F1111] mb-3 leading-tight font-sans">
                      {titles[groupIdx] || titles[0]}
                    </h3>
                    
                    <div className="grid grid-cols-2 gap-4 flex-1 w-full relative">
                      {group.map((prod, idx) => (
                        <div 
                          key={idx} 
                          className="flex flex-col cursor-pointer hover:opacity-90 group"
                          onClick={() => navigate(`/marketplace?product=${prod.id}`)}
                        >
                          <div className="aspect-square bg-gray-50 flex items-center justify-center mb-1 overflow-hidden p-2">
                            {prod.images?.[0] ? (
                              <img src={prod.images[0]} alt={prod.title} className="w-full h-full object-contain mix-blend-multiply" />
                            ) : (
                              <ShoppingBag className="w-8 h-8 text-gray-300" />
                            )}
                          </div>
                          <span className="text-xs text-[#0F1111] leading-tight line-clamp-1 mt-1 font-sans group-hover:text-[#C45500] group-hover:underline">
                            {prod.title}
                          </span>
                        </div>
                      ))}
                      {group.length < 4 && (
                        <div className="col-span-1 border border-dashed border-gray-100 aspect-square flex items-center justify-center bg-gray-50/50 rounded-md">
                          <span className="text-[9px] text-gray-400 text-center font-bold uppercase tracking-tighter">Plis<br/>pwodwi<br/>an wout</span>
                        </div>
                      )}
                    </div>

                    <div className="mt-4 pt-1 w-full">
                      <a href="/marketplace" className="text-sm font-sans text-[#007185] hover:text-[#C45500] hover:underline block w-full text-left">
                        Shop kounye a
                      </a>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Featured Products Section (Direct from Marketplace) */}
      {!loading && marketplaceProducts.length > 0 && (
        <section className="py-12 bg-white" data-testid="featured-products">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between mb-8">
                <div>
                    <h2 className="text-3xl font-black text-gray-900 leading-none mb-2 tracking-tighter italic">
                        Seleksyon <span className="text-[#FF9900]">Epesyal</span>
                    </h2>
                    <p className="text-gray-500 font-bold">Dènye pwodwi ki afiche sou Marketplace la</p>
                </div>
                <a href="/marketplace" className="hidden sm:inline-flex text-[#007185] hover:text-[#C45500] font-sans font-bold hover:underline">
                    Wè tout pwodwi yo <ExternalLink className="w-4 h-4 ml-1" />
                </a>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {marketplaceProducts.slice(0, 8).map(product => (
                  <div
                      key={product.id}
                      onClick={() => navigate(`/marketplace?product=${product.id}`)}
                      className="rounded-2xl overflow-hidden hover:shadow-xl transition-all group border border-gray-100 shadow-sm cursor-pointer bg-white flex flex-col h-full"
                  >
                      <div className="aspect-[4/3] bg-gray-50 flex items-center justify-center relative overflow-hidden">
                          {product.images?.[0] ? (
                              <img src={product.images[0]} alt={product.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                          ) : (
                              <Package className="w-12 h-12 text-gray-200" />
                          )}
                          <div className="absolute top-3 left-3 flex flex-col gap-2">
                              {product.condition === 'new' && (
                                  <span className="bg-[#FF9900] text-white px-3 py-1 text-[10px] font-black uppercase tracking-wider rounded-md shadow-md">NEW</span>
                              )}
                          </div>
                      </div>
                      <div className="p-5 flex flex-col flex-1">
                          <h3 className="font-bold text-lg mb-2 text-gray-900 line-clamp-2 leading-tight group-hover:text-[#FF9900] transition-colors">{product.title}</h3>
                          <div className="mt-auto flex items-end justify-between">
                              <div>
                                  <span className="text-2xl font-black text-gray-900">{product.price_htg.toLocaleString()}</span>
                                  <span className="text-xs font-black text-gray-400 ml-1 uppercase">HTG</span>
                              </div>
                              <div className="flex gap-2">
                                  <button
                                      onClick={(e) => {
                                          e.stopPropagation();
                                          navigate(`/store/${product.seller_slug || product.seller_id}`);
                                      }}
                                      className="h-10 px-3 text-[9px] font-black text-[#FF9900] bg-white border border-[#FF9900]/20 hover:bg-[#FF9900]/10 uppercase tracking-widest rounded-xl transition-colors"
                                  >
                                      MAGAZEN
                                  </button>
                                  <button
                                      onClick={(e) => {
                                          e.stopPropagation();
                                          const productLink = `${window.location.origin}/marketplace?product=${product.id}`;
                                          window.open(`https://wa.me/?text=${encodeURIComponent("Tcheke sa sou BVGLink: " + product.title + " " + productLink)}`, "_blank");
                                      }}
                                      className="w-10 h-10 rounded-full bg-green-50 text-green-600 flex items-center justify-center hover:bg-green-100 transition-colors"
                                  >
                                      <MessageCircle className="w-5 h-5" />
                                  </button>
                              </div>
                          </div>
                      </div>
                  </div>
              ))}
            </div>
            
            <div className="mt-8 text-center sm:hidden">
                <a href="/marketplace" className="inline-flex items-center justify-center bg-gray-100 text-gray-800 font-bold px-6 py-3 rounded-xl w-full">
                    Wè tout pwodwi yo <ExternalLink className="w-4 h-4 ml-2" />
                </a>
            </div>
          </div>
        </section>
      )}

      {/* Gift Cards Section */}
      <section id="cards" className="py-16 bg-gray-50" data-testid="cards-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#232F3E] mb-4">
              Chwazi Kat Kado Ou
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Nou gen kat kado Amazon disponib nan plizyè valè. Chwazi sa ki pi bon pou ou!
            </p>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="loading-spinner" data-testid="loading-spinner"></div>
            </div>
          ) : (
            <>
              {/* Predefined Cards */}
              {cards.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 stagger-children mb-12">
                  {cards.map((card) => (
                    <GiftCardItem
                      key={card.id}
                      card={card}
                    />
                  ))}
                </div>
              )}

              {cards.length === 0 && (
                <div className="empty-state mt-8" data-testid="no-cards-message">
                  <CreditCard className="w-16 h-16 mb-4" />
                  <p className="text-xl font-semibold">Pa gen kat prè-defini disponib pou kounye a.</p>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 bg-white" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#232F3E] mb-4">
              Kijan Li Fonksyone
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { step: "1", title: "Chwazi Valè", desc: "Chwazi kat kado ki gen valè ou bezwen" },
              { step: "2", title: "Antre Nimewo", desc: "Mete nimewo telefòn Moncash ou" },
              { step: "3", title: "Peye ak Moncash", desc: "Konfime peman an ak Moncash" },
              { step: "4", title: "Jwenn Kòd Ou", desc: "Resevwa kòd kat kado a touswit" }
            ].map((item, index) => (
              <div key={index} className="text-center" data-testid={`step-${index + 1}`}>
                <div className="w-16 h-16 rounded-full bg-[#FF9900] text-black font-bold text-2xl flex items-center justify-center mx-auto mb-4">
                  {item.step}
                </div>
                <h3 className="font-bold text-[#232F3E] mb-2">{item.title}</h3>
                <p className="text-gray-600 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Badges */}
      <section className="py-12 bg-[#232F3E]" data-testid="trust-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center items-center gap-8 text-white/80">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-[#FF9900]" />
              <span>Kat Otantik Amazon</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-[#FF9900]" />
              <span>Peman Sekirize</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-[#FF9900]" />
              <span>Sipò 24/7</span>
            </div>
          </div>
        </div>
      </section>

      {/* Mobile Apps Download Section */}
      <section className="py-16 bg-gradient-to-tr from-[#131921] to-[#232F3E] text-white overflow-hidden relative" data-testid="apps-section">
        {/* Background Decorative Rings */}
        <div className="absolute top-0 right-0 -mr-20 -mt-20 w-80 h-80 rounded-full border-4 border-white/5 opacity-50 blur-3xl mix-blend-screen pointer-events-none"></div>
        <div className="absolute bottom-0 left-0 -ml-20 -mb-20 w-64 h-64 rounded-full border-4 border-[#FF9900]/10 opacity-50 blur-3xl mix-blend-screen pointer-events-none"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-3xl md:text-5xl font-black text-white mb-6 tracking-tighter drop-shadow-xl inline-block relative">
            <span className="bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">Pase nan yon lòt nivo avèk</span> <span className="text-[#FF9900] underline decoration-[#FF9900]/30 decoration-8 underline-offset-4">BVGLink</span>!
          </h2>
          <p className="text-gray-300 text-lg md:text-xl max-w-3xl mx-auto mb-10 font-bold leading-relaxed drop-shadow-md bg-white/5 backdrop-blur-sm p-4 rounded-2xl border border-white/10 shadow-inner">
            Gere biznis ou kèlkeswa kote w ye toudwat sou telefòn ou! Telechaje aplikasyon ofisyèl nou yo epi kòmanse resevwa avètisman ak jere lòd an tan reyèl. <br className="hidden md:block"/> <span className="text-[13px] font-normal italic text-yellow-200 mt-2 block">(Piske se APK Android, sonje debloke "Autoriser les sources inconnues" nan Paramèt ou si yo mande l).</span>
          </p>

          <div className="flex flex-col sm:flex-row justify-center items-center gap-6 md:gap-10">
            {/* Driver App Button */}
            <a 
              href="https://expo.dev/artifacts/eas/stqFbFbc7Ld8xWQ3FjqcYz.apk"
              className="group relative flex items-center justify-center gap-5 bg-gradient-to-br from-[#8E44AD] to-[#732D91] text-white px-8 py-5 rounded-3xl font-black text-xl md:text-2xl hover:brightness-110 transition-all hover:-translate-y-2 hover:scale-105 shadow-[0_10px_30px_rgba(142,68,173,0.5)] w-full sm:w-auto border border-white/20"
              data-testid="download-driver"
            >
              <div className="bg-white/20 rounded-full p-4 group-hover:rotate-12 transition-transform shadow-inner">
                <ExternalLink className="w-8 h-8" />
              </div>
              <div className="text-left flex flex-col items-start relative top-0.5">
                <span className="text-[11px] font-bold text-violet-200 uppercase tracking-widest mb-0.5 bg-black/20 px-2 py-0.5 rounded-md">APK Android</span>
                <span className="leading-none tracking-tight">Telechaje Chofè</span>
              </div>
            </a>

            {/* Seller App Button */}
            <a 
              href="https://bvglinkbiznis.com/pos.apk"
              className="group relative flex items-center justify-center gap-5 bg-gradient-to-br from-[#27AE60] to-[#1E8449] text-white px-8 py-5 rounded-3xl font-black text-xl md:text-2xl hover:brightness-110 transition-all hover:-translate-y-2 hover:scale-105 shadow-[0_10px_30px_rgba(39,174,96,0.5)] w-full sm:w-auto border border-white/20"
              data-testid="download-seller"
            >
              <div className="bg-white/20 rounded-full p-4 group-hover:-rotate-12 transition-transform shadow-inner">
                <ShoppingBag className="w-8 h-8" />
              </div>
              <div className="text-left flex flex-col items-start relative top-0.5">
                <span className="text-[11px] font-bold text-green-200 uppercase tracking-widest mb-0.5 bg-black/20 px-2 py-0.5 rounded-md">APK Android</span>
                <span className="leading-none tracking-tight">Telechaje Machann</span>
              </div>
            </a>
          </div>
        </div>
      </section>

      <Footer />
      <VirtualCardModal isOpen={showMeruModal} onClose={() => setShowMeruModal(false)} />
      <CryptoRechargeModal isOpen={showCryptoModal} onClose={() => setShowCryptoModal(false)} />
    </div>
  );
};

